import { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import history from '../../../history'
import { defineMessages, useIntl } from 'react-intl'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { sendRefiRequestForClient } from '../../../actions/refinance'
import { selectCustomerIsAgent, selectCustomerHasSponsor } from '../../../store/selectors/customerProfile'
import { selectContactFirstName } from '../../../store/selectors/contact'
import {
  selectRefiRequested,
  selectRefiRequestSuccess,
  selectRefiRequestFailure
} from '../../../store/selectors/refinance'
import { If, OutreachButton, OverlayPanel } from '../..'
import { IconCommentRight } from '../../shared/Icon'
import { buildRefiKey } from '../../../constants/refinance'
import RefiRateRequestPanel from './RefiRateRequestPanel'
import { useTrackingContext } from '../../../providers/tracking'
import { CUSTOMER_MESSAGES } from 'src/constants/customerMessages'

const propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  years: PropTypes.number
}

const messages = defineMessages({
  success: {
    id: 'RefiRequest.success',
    defaultMessage: 'Great! {contactFirstName} will be in contact shortly'
  },
  failure: {
    id: 'RefiRequest.failure',
    defaultMessage: 'Request failed, unable to contact {contactFirstName}.'
  },
  refiDetailRate: {
    id: 'Questions.refinance.refiDetailRate',
    defaultMessage: 'Get your rate'
  }
})

const RefiRequestButton = ({ className, type, years }) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()

  const contactFirstName = useSelector(selectContactFirstName)
  const customerIsAgent = useSelector(selectCustomerIsAgent)
  const customerHasCospo = useSelector(selectCustomerHasSponsor)

  const requested = useSelector(selectRefiRequested)
  const success = useSelector(selectRefiRequestSuccess)
  const failure = useSelector(selectRefiRequestFailure)

  const refiKey = buildRefiKey({ type, years })
  const originPath = OVERLAY_HELPERS.refinance.buildPath(refiKey)
  const cospoRequestPath = OVERLAY_HELPERS.refiRateRequest.buildPath()

  const sendRefiRequest = useCallback(() => {
    const message = intl.formatMessage(CUSTOMER_MESSAGES.REFINANCING.message, { years: years, type: type })
    dispatch(sendRefiRequestForClient(message, trackingClient))
  }, [type, years, dispatch, trackingClient])

  const navigateCospoPanel = useCallback(() => {
    history.push(cospoRequestPath)
  }, [cospoRequestPath])

  const handleClick = useCallback(() => {
    if (customerIsAgent && customerHasCospo) {
      navigateCospoPanel()
    } else {
      sendRefiRequest()
    }
  }, [customerIsAgent, customerHasCospo, navigateCospoPanel, sendRefiRequest])

  const resultMessage = success
    ? intl.formatMessage(messages.success, {
        contactFirstName: contactFirstName
      })
    : intl.formatMessage(messages.failure, {
        contactFirstName: contactFirstName
      })

  return (
    <If condition={type && years}>
      <OutreachButton
        className={className}
        contactFirstName={contactFirstName}
        iconStart={IconCommentRight}
        requested={requested}
        success={success}
        failure={failure}
        buttonMessage={messages.refiDetailRate}
        resultMessage={resultMessage}
        handleClick={handleClick}
      />
      <OverlayPanel
        back={originPath}
        path={cospoRequestPath}
        title={OVERLAY_HELPERS.getTitle('refiRateRequest', intl.formatMessage)}
        render={() => <RefiRateRequestPanel sendRequest={sendRefiRequest} />}
      />
    </If>
  )
}

RefiRequestButton.propTypes = propTypes

export default RefiRequestButton
